import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

export const _frontmatter = {
  "title": "Ownership | How revenue works",
  "description": "Natural resource ownership in the United States is closely tied to land ownership.",
  "includeToc": true,
  "tags": ["How revenue works", "land ownership", "private land", "federal land", "state and local land", "indian land", "native american", "natural resources", "split ownership"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const GlossaryTerm = makeShortcode("GlossaryTerm");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "/sites/About-Page-Updates/how-revenue-works/"
      }}>{`How revenue works`}</a>{` /`}</p>
    <h1 {...{
      "id": "Ownership",
      "style": {
        "position": "relative"
      }
    }}>{`Ownership`}</h1>
    <p>{`Natural resource ownership in the United States is closely tied to land ownership.`}</p>
    <h2 {...{
      "id": "Land-ownership",
      "style": {
        "position": "relative"
      }
    }}>{`Land ownership`}</h2>
    <p>{`There are four main types of land owners: `}</p>
    <ul>
      <li parentName="ul">{`citizens and corporations`}</li>
      <li parentName="ul">{`the federal government `}</li>
      <li parentName="ul">{`state and local governments`}</li>
      <li parentName="ul">{`Native American tribes and individuals `}</li>
    </ul>
    <p>{`There are two types of owners for submerged lands under the ocean: `}</p>
    <ul>
      <li parentName="ul">{`states`}</li>
      <li parentName="ul">{`the federal government`}</li>
    </ul>
    <h3 {...{
      "id": "Private-lands",
      "style": {
        "position": "relative"
      }
    }}>{`Private lands`}</h3>
    <p>{`Private lands are owned by private citizens or corporations.`}</p>
    <h3 {...{
      "id": "Federal-lands",
      "style": {
        "position": "relative"
      }
    }}>{`Federal lands`}</h3>
    <p>{`The federal government owns or has jurisdiction over `}<a parentName="p" {...{
        "href": "http://fas.org/sgp/crs/misc/R42346.pdf"
      }}>{`federal lands`}</a>{`. Federal lands include:`}</p>
    <ul>
      <li parentName="ul">{`Public domain lands ceded to the U.S. by treaty, purchase, or conquest.`}</li>
      <li parentName="ul">{`Lands that the federal government acquired. Acquisition methods include: purchased by, given to, exchanged with, or transferred through condemnation proceedings to the federal government.`}</li>
      <li parentName="ul">{`Military acquired lands purchased by the federal government under military acquisition laws.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.boem.gov/OCS-Lands-Act-History/"
        }}>{`Outer Continental Shelf`}</a>{` submerged lands located farther than three miles off a state’s coastline. For Texas and western Florida, this is three marine leagues into the Gulf of Mexico.`}</li>
    </ul>
    <h3 {...{
      "id": "State-and-local-lands",
      "style": {
        "position": "relative"
      }
    }}>{`State and local lands`}</h3>
    <p>{`State and local lands are owned by state or local governments, including:`}</p>
    <ul>
      <li parentName="ul">{`Lands owned by a particular state.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.boem.gov/sites/default/files/uploadedFiles/BOEM/Oil_and_Gas_Energy_Program/Leasing/Outer_Continental_Shelf/Lands_Act_History/submerged.pdf"
        }}>{`State submerged lands`}</a>{` under the ocean from a state’s coast to three miles out into the ocean. For Texas and western Florida, this is from the coast out to three marine leagues into the Gulf of Mexico.`}</li>
      <li parentName="ul">{`Lands owned by a local government, such as a county.`}</li>
    </ul>
    <h3 {...{
      "id": "Native-American-lands",
      "style": {
        "position": "relative"
      }
    }}>{`Native American lands`}</h3>
    <p>{`Native American lands include:`}</p>
    <ul>
      <li parentName="ul">{`Tribal lands held in trust by the federal government for a tribe’s use.`}</li>
      <li parentName="ul">{`Allotments held in trust by the federal government for individual Native American use.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://dnr.alaska.gov/mlw/trails/17b/corpindex.cfm"
        }}>{`Alaska Native Corporation`}</a>{` lands in Alaska. Twelve regional Alaska Native Corporations receive rights to some surface lands and to natural resources below the surface. In addition, certain village-level Alaska Native Corporations hold additional surface land rights.`}</li>
    </ul>
    <h2 {...{
      "id": "Natural-resource-ownership",
      "style": {
        "position": "relative"
      }
    }}>{`Natural resource ownership`}</h2>
    <p>{`The following entities can own land and the oil, gas, coal, and other minerals below the surface:`}</p>
    <ul>
      <li parentName="ul">{`private individuals `}</li>
      <li parentName="ul">{`corporations`}</li>
      <li parentName="ul">{`governments (including federal, state, local, and tribal governments) `}</li>
    </ul>
    <p>{`Widespread private ownership of these resources makes the U.S. different. In nearly every other country, these resources simply belong to the national government.`}</p>
    <br />
    <p>{`Natural resource ownership has historical roots in the 19th century. During this time, the federal government passed homestead and development acts to encourage settlement in the West. These acts and the General Mining Law of 1872 allowed for federal public domain lands, and the natural resources within them, to pass to private ownership. `}</p>
    <br />
    <p>{`Starting in the 20th century, the U.S. passed legislation to withdraw specific natural resources and eventually public domain lands from settlement and other development. This legislation preserves these lands and natural resources in federal ownership today.`}</p>
    <h3 {...{
      "id": "Split-ownership",
      "style": {
        "position": "relative"
      }
    }}>{`Split ownership`}</h3>
    <p>{`Sometimes the land’s surface owner is different from the owner of the minerals in the ground below. The party that owns the land’s surface has `}<GlossaryTerm mdxType="GlossaryTerm">{`surface rights`}</GlossaryTerm>{`. The party that owns the natural resources in the ground has `}<GlossaryTerm mdxType="GlossaryTerm">{`subsurface rights`}</GlossaryTerm>{`. When ownership is divided in this way, it is referred to as a `}<a parentName="p" {...{
        "href": "https://www.blm.gov/programs/energy-and-minerals/oil-and-gas/leasing/split-estate"
      }}>{`split estate`}</a>{`. There are 57 million acres of land in the U.S. where the federal government owns oil, gas, coal, and other minerals below the surface. Another party, mostly citizens or corporations, owns the surface land above. Land and mineral ownership can become quite complicated. Often, a combination of private landholders, the federal government, a state government, or Native American tribes own the span of a single mine or field.`}</p>
    <br />
    <p>{`When it comes to the natural resources found off the coast, the federal government and state governments split ownership. In general, states have primary authority and natural resource ownership in the three-mile area extending outward from their coasts. The federal government owns oil, gas, and minerals located in the submerged lands on the Outer Continental Shelf. Submerged land ownership extends from the states’ offshore boundaries out to at least 200 nautical miles from the shore.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      